if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();

        this.isSyncingQuantity = false;
        this.variantChangeUnsubscriber = undefined;
        this.quantityUpdateUnsubscriber = undefined;
        this.cartUpdateUnsubscriber = undefined;
        this.cartErrorUnsubscriber = undefined;
        this.submitSyncFrame = null;
        this.abortController = null;
      }

      connectedCallback() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.sectionId = this.dataset.sectionId;
        this.productInfo = document.querySelector(`#MainProduct-${this.sectionId}`);
        if (!this.productInfo) return;

        this.mainQuantityInput = document.getElementById(`Quantity-${this.sectionId}`);
        this.stickyQtyDisplay = document.getElementById(`StickyAtcQuantityDisplay-${this.sectionId}`);
        this.stickyQtyHidden = document.getElementById(`StickyAtcQuantityHidden-${this.sectionId}`);
        this.stickyDecreaseButton = this.querySelector('[data-action="decrease"]');
        this.stickyIncreaseButton = this.querySelector('[data-action="increase"]');
        this.mainSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.stickySubmitButton = this.querySelector('.sticky-atc-bar__submit');
        this.stickySubmitText = this.stickySubmitButton?.querySelector('.sticky-atc-bar__submit-text');
        this.stickyPrice = document.getElementById(`StickyAtcPrice-${this.sectionId}`);
        this.stickyImage = this.querySelector('.sticky-atc-bar__image-img');
        this.stickyTitle = this.querySelector('.sticky-atc-bar__title');

        this.initVisibility(signal);
        this.initQuantitySync(signal);
        this.initSubmitTrigger(signal);
        this.initContentSync(signal);
        this.initPubSub();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.abortController = null;
        this.variantChangeUnsubscriber?.();
        this.quantityUpdateUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        this.cancelSubmitSync();
      }

      initVisibility(signal) {
        if (!this.mainSubmitButton) return;

        this.boundUpdateVisibility = this.updateVisibility.bind(this);
        window.addEventListener('scroll', this.boundUpdateVisibility, { passive: true, signal });
        window.addEventListener('resize', this.boundUpdateVisibility, { passive: true, signal });
        this.updateVisibility();
      }

      updateVisibility() {
        if (!this.mainSubmitButton) return;

        const rect = this.mainSubmitButton.getBoundingClientRect();
        const isIntersecting = rect.top < window.innerHeight && rect.bottom > 0;
        const shouldShow = !isIntersecting;

        this.classList.toggle('is-visible', shouldShow);
        this.toggleAttribute('hidden', !shouldShow);
      }

      initQuantitySync(signal) {
        if (!this.mainQuantityInput || !this.stickyQtyDisplay || !this.stickyDecreaseButton || !this.stickyIncreaseButton) {
          return;
        }

        this.syncQuantityFromMain();

        this.mainQuantityInput.addEventListener(
          'change',
          () => {
            if (this.isSyncingQuantity) return;
            this.syncQuantityFromMain();
          },
          { signal }
        );

        this.stickyDecreaseButton.addEventListener('click', () => this.stepQuantity(-1), { signal });
        this.stickyIncreaseButton.addEventListener('click', () => this.stepQuantity(1), { signal });
      }

      getQuantityMin() {
        const minAttr = this.mainQuantityInput?.min ?? this.mainQuantityInput?.dataset?.min;
        const parsed = parseInt(minAttr, 10);
        return Number.isNaN(parsed) ? 1 : parsed;
      }

      getQuantityMax() {
        const maxAttr = this.mainQuantityInput?.max ?? this.mainQuantityInput?.dataset?.max;
        if (maxAttr === null || maxAttr === undefined || maxAttr === '') return null;
        const parsed = parseInt(maxAttr, 10);
        return Number.isNaN(parsed) ? null : parsed;
      }

      getQuantityStep() {
        const stepAttr = this.mainQuantityInput?.step ?? this.mainQuantityInput?.dataset?.step;
        const parsed = parseInt(stepAttr, 10);
        return Number.isNaN(parsed) ? 1 : parsed;
      }

      getQuantityCurrent() {
        const parsed = parseInt(this.mainQuantityInput?.value, 10);
        return Number.isNaN(parsed) ? this.getQuantityMin() : parsed;
      }

      updateQuantityButtons() {
        const current = this.getQuantityCurrent();
        const min = this.getQuantityMin();
        const max = this.getQuantityMax();

        const isAtMin = current <= min;
        const isAtMax = max !== null && current >= max;

        this.stickyDecreaseButton.classList.toggle('disabled', isAtMin);
        this.stickyIncreaseButton.classList.toggle('disabled', isAtMax);

        this.stickyDecreaseButton.disabled = isAtMin;
        this.stickyIncreaseButton.disabled = isAtMax;
      }

      syncQuantityFromMain() {
        if (!this.mainQuantityInput) return;
        const qty = this.getQuantityCurrent();

        this.isSyncingQuantity = true;
        if (this.stickyQtyHidden) this.stickyQtyHidden.value = String(qty);

        if (this.stickyQtyDisplay) {
          this.stickyQtyDisplay.textContent = qty < 10 ? `0${qty}` : String(qty);
        }
        this.updateQuantityButtons();
        this.isSyncingQuantity = false;
      }

      setMainQuantity(value) {
        if (!this.mainQuantityInput) return;
        this.isSyncingQuantity = true;
        this.mainQuantityInput.value = value;
        this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.isSyncingQuantity = false;
        this.syncQuantityFromMain();
      }

      stepQuantity(direction) {
        const current = this.getQuantityCurrent();
        const min = this.getQuantityMin();
        const max = this.getQuantityMax();
        const step = this.getQuantityStep();

        let next = current + direction * step;

        if (next < min) next = min;
        if (max !== null && next > max) next = max;

        if (next === current) return;
        this.setMainQuantity(next);
      }

      initSubmitTrigger(signal) {
        if (!this.mainSubmitButton || !this.stickySubmitButton) return;

        this.stickySubmitButton.addEventListener(
          'click',
          () => {
            if (this.mainSubmitButton.disabled || this.mainSubmitButton.getAttribute('aria-disabled') === 'true') {
              return;
            }
            this.mainSubmitButton.click();
          },
          { signal }
        );
      }

      initContentSync(signal) {
        this.syncSubmitButton();
        this.syncPrice();
        this.syncImage();

        const titleElement = this.productInfo?.querySelector('.product__title h1');
        if (titleElement && this.stickyTitle) {
          this.stickyTitle.textContent = titleElement.textContent.trim();
        }

        this.initMediaSync(signal);
        this.initSubmitButtonSync(signal);
      }

      initSubmitButtonSync(signal) {
        if (!this.mainSubmitButton) return;

        this.mainSubmitButton.addEventListener(
          'click',
          () => {
            this.syncSubmitButton();
            this.startSubmitSyncWhileLoading();
          },
          { signal }
        );
      }

      startSubmitSyncWhileLoading() {
        this.cancelSubmitSync();

        const sync = () => {
          this.syncSubmitButton();
          if (this.mainSubmitButton?.classList.contains('loading')) {
            this.submitSyncFrame = requestAnimationFrame(sync);
          } else {
            this.submitSyncFrame = null;
          }
        };

        this.submitSyncFrame = requestAnimationFrame(sync);
      }

      scheduleSubmitButtonSync() {
        if (this.mainSubmitButton?.classList.contains('loading')) {
          if (!this.submitSyncFrame) this.startSubmitSyncWhileLoading();
          return;
        }

        this.syncSubmitButton();
      }

      cancelSubmitSync() {
        if (this.submitSyncFrame) {
          cancelAnimationFrame(this.submitSyncFrame);
          this.submitSyncFrame = null;
        }
      }

      initMediaSync(signal) {
        const mediaGallery = this.productInfo?.querySelector('media-gallery');
        if (mediaGallery) {
          const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
          viewer?.addEventListener('slideChanged', () => this.syncImage(), { signal });

          mediaGallery.querySelectorAll('[data-target] button').forEach((button) => {
            button.addEventListener('click', () => requestAnimationFrame(() => this.syncImage()), { signal });
          });
        }

        this.productInfo?.querySelectorAll('.mobile-product-images slider-component').forEach((slider) => {
          slider.addEventListener(
            'slideChanged',
            (event) => {
              const img = event.detail?.currentElement?.querySelector('img');
              if (img?.src && this.stickyImage) {
                this.stickyImage.src = img.currentSrc || img.src;
                if (img.alt) this.stickyImage.alt = img.alt;
                return;
              }
              this.syncImage();
            },
            { signal }
          );
        });
      }

      initPubSub() {
        if (typeof subscribe !== 'function') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data?.sectionId !== this.sectionId) return;
          this.syncPrice();
          this.syncImage();
          this.syncSubmitButton();
          this.syncQuantityFromMain();
        });

        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
          this.syncQuantityFromMain();
        });

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source !== 'product-form') return;
          // cartUpdate fires before product-form finally() removes loading;
          // keep rAF polling so sticky state syncs once loading ends.
          this.scheduleSubmitButtonSync();
        });

        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source !== 'product-form') return;
          this.scheduleSubmitButtonSync();
        });
      }

      syncPrice() {
        if (!this.stickyPrice) return;

        const priceContainer = document.getElementById(`price-${this.sectionId}`);
        if (!priceContainer) return;

        this.stickyPrice.innerHTML = priceContainer.innerHTML;
      }

      syncImage() {
        if (!this.stickyImage) return;

        const activeImage = this.productInfo?.querySelector(
          'media-gallery .product__media-item.is-active img, .mobile-product-images .product__media-item.is-active img'
        );

        const fallbackImage = this.productInfo?.querySelector('media-gallery img, .mobile-product-images img');

        const image = activeImage?.src ? activeImage : fallbackImage;

        if (!image?.src) return;

        this.stickyImage.src = image.currentSrc || image.src;
        if (image.alt) this.stickyImage.alt = image.alt;
      }

      syncSubmitButton() {
        if (!this.mainSubmitButton || !this.stickySubmitButton || !this.stickySubmitText) return;

        const mainText = this.mainSubmitButton.querySelector('span');
        const isLoading = this.mainSubmitButton.classList.contains('loading');
        const isDisabled =
          this.mainSubmitButton.hasAttribute('disabled') ||
          this.mainSubmitButton.getAttribute('aria-disabled') === 'true';

        this.stickySubmitButton.classList.toggle('loading', isLoading);
        this.stickySubmitButton.toggleAttribute('disabled', isDisabled);

        if (mainText) {
          const addToCartText = (window.variantStrings?.addToCart || 'Add to cart').trim();
          const buttonText = mainText.textContent.trim();

          if (buttonText.includes(addToCartText)) {
            const priceEl =
              mainText.querySelector('.price-item--regular') ||
              mainText.querySelector('.price-item') ||
              document.querySelector(`#price-${this.sectionId} .price-item:not(.visually-hidden)`);

            const priceText = priceEl?.textContent?.trim();
            if (priceText) {
              this.stickySubmitText.textContent = `${addToCartText} - ${priceText}`;
              return;
            }
          }

          this.stickySubmitText.textContent = buttonText;
        }
      }
    }
  );
}
